import { BsTruck } from "react-icons/bs"
import { useRouter } from "next/router"
import { CiLocationOn } from "react-icons/ci"
import { TbPlaneDeparture } from "react-icons/tb"
import { AiOutlineClockCircle } from "react-icons/ai"
import { BsArrowsFullscreen } from "react-icons/bs"
import { AiOutlineHeart } from "react-icons/ai"
import { AiFillCheckCircle } from "react-icons/ai"

import PrimaryButton from "../../components/buttons/PrimaryButton"

export default function PetTransportFactors({
  metroName,
}: {
  metroName: string
}) {
  const router = useRouter()

  return (
    <section className="bg-[#f0f4fC] py-10 mt-10 px-4 ">
      <section className="w-full mx-auto max-w-[1289px] flex flex-col items-center ">
        <h2 className="mb-3 font-montserrat font-semibold text-center text-4xl text-gray8">
          Factors affecting the cost of shipping pets
        </h2>
        <p className="mb-11 text-center text-lg text-gray7 font-normal">
          Here's what you need to know about safely transporting your pet to{" "}
          {metroName}.
        </p>
        <article className="w-full grid grid-cols-1 lg:grid-cols-2 gap-8">
          <section className="bg-white p-6 space-y-4 rounded-xl drop-shadow-card">
            <div className="flex items-center space-x-2.5">
              <div className="flex justify-center items-center p-2 rounded-full bg-[#f0f4fC]">
                <BsTruck className="text-3xl" />
              </div>
              <h3 className="text-2xl font-montserrat font-semibold m-0">
                Mode of Transportation
              </h3>
            </div>
            <section className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="p-4 rounded-lg bg-[#f0f4fC]">
                <div className="flex space-x-2.5">
                  <BsTruck className="text-xl" />
                  <p className="font-semibold">Ground travel</p>
                </div>
                <div className="flex flex-wrap">
                  <p className="w-fit bg-white rounded-md px-2 py-1 mr-2.5 whitespace-nowrap">
                    Cost
                    <span className="font-semibold"> $</span>
                  </p>
                  <p className="w-fit bg-white rounded-md px-2 py-1 whitespace-nowrap">
                    Time{" "}
                    <span className="font-semibold">
                      <AiOutlineClockCircle className="inline-block" />
                    </span>
                    <span className="font-light">
                      {" "}
                      <AiOutlineClockCircle className="inline-block" />{" "}
                      <AiOutlineClockCircle className="inline-block" />
                    </span>
                  </p>
                </div>
              </div>
              <div className="p-4 rounded-lg bg-[#f0f4fC]">
                <div className="flex space-x-2.5">
                  <TbPlaneDeparture className="text-xl" />
                  <p className="font-semibold">Air travel</p>
                </div>
                <div className="flex space-x-2.5">
                  <p className="w-fit bg-white rounded-md px-2 py-1">
                    Cost
                    <span className="font-semibold"> $ $ $</span>
                  </p>
                  <p className="w-fit bg-white rounded-md px-2 py-1">
                    Time{" "}
                    <span className="font-semibold">
                      <AiOutlineClockCircle className="inline-block" />
                    </span>
                  </p>
                </div>
              </div>
            </section>
            <p>
              Ground and air travel have different associated costs. Flying is
              typically the fastest option, but also the most expensive and
              taxing on your pet.
            </p>
          </section>
          <section className="bg-white p-6 space-y-4 rounded-xl drop-shadow-card">
            <div className="flex items-center space-x-2.5">
              <div className="flex justify-center items-center p-2 rounded-full bg-[#f0f4fC]">
                <CiLocationOn className="text-3xl" />
              </div>
              <h3 className="text-2xl font-montserrat font-semibold m-0">
                Travel Distance
              </h3>
            </div>
            <p>
              The further you need to transport your pet, the more it'll cost.
              If you're using ground transportation, you'll have to factor in
              fuel costs, lodging, meals on the road and wear-and-tear on your
              vehicle. These costs are applicable whether you drive the pet
              yourself or you hire a transporter through CitizenShipper.
              Airlines charge more for longer plane rides as well. This is due
              to the cost of fuel and the need compensate pilots and flight
              attendants.
            </p>
          </section>
          <section className="bg-white p-6 space-y-4 rounded-xl drop-shadow-card">
            <div className="flex items-center space-x-2.5">
              <div className="flex justify-center items-center p-3 rounded-full bg-[#f0f4fC]">
                <BsArrowsFullscreen className="text-2xl" />
              </div>
              <p className="text-2xl font-montserrat font-semibold m-0">
                Pet size
              </p>
            </div>
            <p>
              Smaller pets are easier – and less expensive – to transport. If
              you’re flying your pet to {metroName}, they’ll need to travel in
              the cargo hold if they’re over 20 pounds. You’ll also need to buy
              a USDA-approved crate, which can cost around $200. With ground
              transport, it’s easier for drivers to accommodate small pets.
            </p>
          </section>
          <section className="bg-white p-6 space-y-4 rounded-xl drop-shadow-card">
            <div className="flex items-center space-x-2.5">
              <div className="flex justify-center items-center p-2 rounded-full bg-[#f0f4fC]">
                <AiOutlineHeart className="text-3xl" />
              </div>
              <p className="text-2xl font-montserrat font-semibold m-0">
                Health clearance and travel equipment
              </p>
            </div>
            <p>
              The first step to transporting your pet to {metroName} is ensuring
              they’re healthy enough to travel. Most species require a
              vet-issued health certificate to cross state lines. This will cost
              $50 to $300. You’ll likely need to purchase some equipment too –
              such as a crate and travel supplies.
            </p>
          </section>
        </article>
        <PrimaryButton
          className="w-full sm:max-w-md lg:max-w-lg mt-12"
          onClick={() => router.push(`/shipments/new/animals`)}
        >
          Get Free Transport Quotes
        </PrimaryButton>
        <p className="mt-3 text-center">
          <AiFillCheckCircle className="inline-block fill-green5 text-xl mr-2" />
          Fast and easy{" "}
          <AiFillCheckCircle className="inline-block fill-green5 text-xl mx-2" />
          Only pay when you book
        </p>
      </section>
    </section>
  )
}
